import moment from 'moment';
import React, { useEffect } from 'react';
import {
    ProviderTile,
    ReferralAction,
    ReferralAttachmentType,
    ReferralAttachments,
    ReferralDisabledAlert,
} from '~/components/Patient/Referrals';
import { ServicesContext } from '~/services';
import { ReferralContext, ReferralContextType } from '~/services/ReferralContext';
import { ProviderType } from '~/util/providerUtils';

export interface ReferralInfoProps {
    planId: number | undefined;
    patient: PatientType;
    planCode: string | undefined;
}

export function ReferralInfo({ planId, patient, planCode }: ReferralInfoProps) {
    const context = React.useContext(ServicesContext);
    const { selectedProvider, clearSelectedProvider } = React.useContext(
        ReferralContext
    ) as ReferralContextType;
    // this component only renders if selectedProvider is not null
    const provider = selectedProvider as ProviderType;

    const [reason, setReason] = React.useState('');
    const notesTemplate = `From: ${moment().format('MM/DD/YYYY')}\nTo: ${moment()
        .add(1, 'y')
        .format('MM/DD/YYYY')}\nVisits: 12\nInstructions: Evaluate & Treat`;
    const [notes, setNotes] = React.useState(notesTemplate);
    const [email, setEmail] = React.useState('');
    const [sendingReferral, setSendingReferral] = React.useState(false);
    const [attachments, setAttachments] = React.useState<ReferralAttachmentType[]>([]);
    const [suggestedEdit, setSuggestedEdit] = React.useState(false);
    const [missingRequiredLocation, setMissingRequiredLocation] = React.useState(false);
    const [selectedLocation, setSelectedLocation] = React.useState<undefined | LocationType>(() => {
        if (provider.locations?.length === 1) {
            return provider.locations[0];
        }
        return provider.locations?.find((x) => x.is_default);
    });

    useEffect(() => {
        if (selectedLocation && missingRequiredLocation) {
            setMissingRequiredLocation(false);
        }
    }, [selectedLocation, missingRequiredLocation]);

    function insertNotes() {
        setNotes(notesTemplate);
    }

    return (
        <>
            <ReferralDisabledAlert provider={provider} setSuggestedEdit={setSuggestedEdit} />
            <div className="row mb-3">
                <div className="col">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={clearSelectedProvider}
                    >
                        Back
                    </button>
                </div>
                <div className="col text-end">
                    {context.user.getExternalAccount('Core App') && (
                        <ReferralAttachments
                            patient={patient}
                            attachments={attachments}
                            setAttachments={setAttachments}
                            sendingReferral={sendingReferral}
                        />
                    )}
                    <ReferralAction
                        patient={patient}
                        provider={provider}
                        planId={planId}
                        reason={reason}
                        notes={notes}
                        email={email}
                        attachments={attachments}
                        sendingReferral={sendingReferral}
                        setSendingReferral={setSendingReferral}
                        selectedLocation={selectedLocation}
                        setMissingRequiredLocation={setMissingRequiredLocation}
                        suggestedEdit={suggestedEdit}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 col-md-6 col-lg-6">
                    <div className="mb-3">
                        <label htmlFor="referral-reason" className="fw-bold mb-1">
                            Reason for Referral
                        </label>
                        <input
                            id="referral-reason"
                            type="text"
                            className="form-control"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div>
                    <div className="mb-3 position-relative">
                        <label htmlFor="referral-notes" className="fw-bold mb-1">
                            Notes to Provider
                        </label>
                        <textarea
                            id="referral-notes"
                            className="form-control"
                            rows={4}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                        {!notes && (
                            <button
                                className="btn btn-sm btn-outline-secondary position-absolute bottom-0 end-0 mb-2 me-2"
                                type="button"
                                onClick={insertNotes}
                            >
                                Visit Template
                            </button>
                        )}
                    </div>
                    <div className="mb-3">
                        <label htmlFor="referral-email" className="fw-bold mb-1">
                            Patient Email
                            <i
                                className="fa-regular fa-question-circle ms-2"
                                title='A referral confirmation will be emailed to the address you enter after you click "Send".'
                            />
                        </label>
                        <input
                            id="referral-email"
                            type="email"
                            className="form-control"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-lg-2 d-none d-lg-block" />
                <ProviderTile
                    provider={provider}
                    showLocations
                    selectedLocation={selectedLocation}
                    setSelectedLocation={setSelectedLocation}
                    missingRequiredLocation={missingRequiredLocation}
                    setSuggestedEdit={setSuggestedEdit}
                    planCode={planCode}
                />
            </div>
        </>
    );
}
